import { type CellValue } from 'ui/atoms/Table/Table.utils'
import type { EditTimeSeriesItem } from 'ui/uiConfig/anchorComponents/EditTimeSeries/EditTimeSeries.types'
import Datetime from 'utils/datetime/datetime'

// The interval one grid row covers. It has to equal the interval the backend aggregates the read
// over, which it decides from an environment variable this client cannot see (aurora-django's
// DBS_DEFAULT_AGGREGATE_DURATION). Both say one hour today, and nothing enforces that they agree:
// if either moved, every cell would show an average over a different span than its row claims.
const GRID_ROW_INTERVAL_MS = 60 * 60 * 1000

/**
 * What one row of this grid represents, in the ISO-8601 duration the save endpoint expects.
 *
 * Expressed in minutes so it is a real function of the row interval: change that constant and this
 * follows it, rather than sitting here describing a row size the grid no longer renders. A single
 * point written while claiming to describe an hour is the bug this whole change exists to fix, so
 * the two must not be able to disagree. `PT60M` and `PT1H` are the same duration to the endpoint,
 * which compares parsed intervals rather than strings.
 */
export const GRID_ROW_DURATION = `PT${GRID_ROW_INTERVAL_MS / 60_000}M`

/** Row 0 of the matrix is the header row and column 0 is the time column. */
const HEADER_ROW_OFFSET = 1
const TIME_COLUMN_OFFSET = 1

/**
 * Snaps a timestamp back to the start of its UTC hour.
 *
 * A write lands on the exact timestamp of its row, and a read matches exactly, so a row at HH:37
 * writes a point no hour-aligned view ever asks for. The date picker already forces minute 0, but
 * the period can also arrive from a hand-edited `startTime` query parameter.
 */
export function alignToHourStart(time: ISODateTime): ISODateTime {
  const timeInMs = new Date(time).getTime()
  return Datetime.toISOString(new Date(Math.floor(timeInMs / GRID_ROW_INTERVAL_MS) * GRID_ROW_INTERVAL_MS))
}

/**
 * The hourly timestamps the grid is built from, one per row. Both ends are snapped to the hour and
 * both are inclusive, so a period of 08:00–10:59 and one of 08:37–10:00 produce the same three rows.
 *
 * Both the rendered rows and the payload sent on save are derived from this list, so a row's
 * position and the timestamp written for it cannot drift apart.
 */
export function buildGridTimes(startTime: ISODateTime, endTime: ISODateTime): ISODateTime[] {
  const firstTimeInMs = new Date(alignToHourStart(startTime)).getTime()
  const lastTimeInMs = new Date(alignToHourStart(endTime)).getTime()
  const times: ISODateTime[] = []

  for (let timeInMs = firstTimeInMs; timeInMs <= lastTimeInMs; timeInMs += GRID_ROW_INTERVAL_MS) {
    times.push(Datetime.toISOString(new Date(timeInMs)))
  }

  return times
}

export type GridCoordinates = {
  /** The data point a grid cell stands for, or `null` if the cell is not an editable value. */
  toDataPoint: (cell: { rowIndex: number; columnIndex: number }) => { dataset_id: number; time: ISODateTime } | null
}

/** The one place the grid's row/column arithmetic lives, so a cell and its data point agree. */
export function buildGridCoordinates(datasets: Dataset[], gridTimes: ISODateTime[]): GridCoordinates {
  return {
    toDataPoint: ({ rowIndex, columnIndex }) => {
      const time = gridTimes[rowIndex - HEADER_ROW_OFFSET]
      const dataset = datasets[columnIndex - TIME_COLUMN_OFFSET]

      if (time === undefined || dataset === undefined) {
        return null
      }

      return { dataset_id: Number(dataset.return_id), time }
    },
  }
}

function buildHeaderRow(datasets: Dataset[], items: EditTimeSeriesItem[]): CellValue[] {
  const dataIdToItem = new Map(items.map((item) => [String(item.data_id), item]))

  return [
    'Time',
    ...datasets.map((dataset) => {
      const item = dataIdToItem.get(dataset.return_id)
      const title = item?.title || '-'
      return item?.sub_title ? `${title}\n${item.sub_title}` : title
    }),
  ]
}

export function datasetToTableMatrix(
  datasets: Dataset[],
  gridTimes: ISODateTime[],
  items: EditTimeSeriesItem[]
): CellValue[][] {
  const timeInMsToGridIndex = new Map(gridTimes.map((time, index) => [new Date(time).getTime(), index]))
  const rows: CellValue[][] = gridTimes.map((time) => [time, ...Array<CellValue>(datasets.length).fill(null)])

  datasets.forEach((dataset, datasetIndex) => {
    dataset.times.forEach((time, valueIndex) => {
      const gridRowIndex = timeInMsToGridIndex.get(new Date(time as string).getTime())
      // A point stored off the hour — written before the period was snapped — belongs to no row.
      // Skipping it keeps the rest of the period readable; it would otherwise take the whole view
      // down for everyone until the point is removed.
      if (gridRowIndex === undefined) {
        return
      }

      const value = dataset.values[valueIndex]
      rows[gridRowIndex][datasetIndex + TIME_COLUMN_OFFSET] = value === null ? null : value.toString()
    })
  })

  return [buildHeaderRow(datasets, items), ...rows]
}
